import { useMemo } from 'react';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useGetRoutesQuery } from '../services/routes.service';
import type { RoutesFilter, RoutesListSharedFilterFields } from '../models';

const ROUTE_NAME_SUGGESTIONS_PAGE_SIZE = 15;

interface UseRouteNameSuggestionsProps {
  appliedSharedFilters: RoutesListSharedFilterFields;
  routeNameSearchTerm: string;
}

const buildRoutesQueryFiltersForNameSuggestions = (
  sharedFields: RoutesListSharedFilterFields,
  trimmedRouteName: string
): RoutesFilter => ({
  ...sharedFields,
  ...(trimmedRouteName.length > 0 ? { routeName: trimmedRouteName } : {}),
});

const useRouteNameSuggestions = ({
  appliedSharedFilters,
  routeNameSearchTerm,
}: UseRouteNameSuggestionsProps) => {
  const trimmedRouteName = routeNameSearchTerm.trim();
  const isQuerySkipped = trimmedRouteName.length === 0;

  const suggestionQueryFilters = useMemo(
    () =>
      buildRoutesQueryFiltersForNameSuggestions(
        appliedSharedFilters,
        trimmedRouteName
      ),
    [appliedSharedFilters, trimmedRouteName]
  );

  const { data, isLoading, isFetching } = useGetRoutesQuery(
    {
      paginated: { page: 1, pageSize: ROUTE_NAME_SUGGESTIONS_PAGE_SIZE },
      filters: suggestionQueryFilters,
    },
    { skip: isQuerySkipped }
  );

  const routeNameOptions: Option[] = useMemo(() => {
    if (!data?.items) return [];
    const uniqueLabels = new Map<string, Option>();
    for (let index = 0; index < data.items.length; index += 1) {
      const route = data.items[index];
      const displayName = (route.name ?? '').trim();
      if (displayName.length === 0) continue;
      if (!uniqueLabels.has(displayName)) {
        uniqueLabels.set(displayName, { label: displayName, data: displayName });
      }
    }
    return [...uniqueLabels.values()];
  }, [data?.items]);

  return {
    routeNameOptions,
    isRouteNamesLoading: isLoading || isFetching,
  };
};

export default useRouteNameSuggestions;
